import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private isBrowser: boolean;
  private measurementId: string;
  private isDebugMode: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private logger: LoggerService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.measurementId = environment.firebase.measurementId;
    this.isDebugMode = !environment.production;
  }

  /**
   * Check if analytics is available
   */
  private get isAnalyticsAvailable(): boolean {
    return (
      this.isBrowser &&
      !!this.measurementId &&
      typeof window !== 'undefined' &&
      typeof window.gtag !== 'undefined'
    );
  }

  /**
   * Track a page view
   * @param pagePath The page path
   * @param pageTitle The page title
   */
  trackPageView(pagePath: string, pageTitle: string): void {
    if (!this.isAnalyticsAvailable) {
      this.logger.debug('GA4 page view not tracked - not available');
      return;
    }

    try {
      this.logger.debug('Tracking GA4 page view:', { pagePath, pageTitle });

      // Send page_view event
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
        page_location: window.location.href,
      });

      // Update config with the current page
      window.gtag('config', this.measurementId, {
        page_path: pagePath,
      });
    } catch (error) {
      this.logger.error('Error tracking page view:', error);
    }
  }

  /**
   * Track a custom event
   * @param eventName The name of the event
   * @param eventParams Additional parameters for the event
   */
  trackEvent(eventName: string, eventParams: Record<string, any> = {}): void {
    if (!this.isAnalyticsAvailable) {
      this.logger.debug(`GA4 event '${eventName}' not tracked - not available`);
      return;
    }

    try {
      this.logger.debug('Tracking GA4 event:', eventName, eventParams);
      window.gtag('event', eventName, eventParams);
    } catch (error) {
      this.logger.error(`Error tracking event ${eventName}:`, error);
    }
  }

  /**
   * Track a search event
   * @param searchTerm The search term
   */
  trackSearch(searchTerm: string): void {
    this.trackEvent('search', { search_term: searchTerm });
  }

  /**
   * Track a fact check view
   * @param factId The ID of the fact check
   * @param factTitle The title of the fact check
   */
  trackFactView(factId: string, factTitle: string): void {
    this.trackEvent('fact_view', {
      fact_id: factId,
      fact_title: factTitle,
    });
  }

  /**
   * Track social media share
   * @param platform The platform shared to (e.g., 'twitter', 'facebook')
   * @param contentId The ID of the content shared
   */
  trackShare(platform: string, contentId: string): void {
    this.trackEvent('share', {
      platform,
      content_id: contentId,
    });
  }
}
